import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { ChatText, TextT } from "@phosphor-icons/react";
import { defaultMyMindItem, myMindItemSchema } from "@stylingcv/schema";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Slider,
  Textarea,
} from "@stylingcv/ui";
import { cn } from "@stylingcv/utils";
import { useForm } from "react-hook-form";
import type { z } from "zod";

import { SectionDialog } from "../sections/shared/section-dialog";

const PALETTE = [
  "#005f73",
  "#0a9396",
  "#94d2bd",
  "#e9d8a6",
  "#ee9b00",
  "#ca6702",
  "#bb3e03",
  "#ae2012",
];

const formSchema = myMindItemSchema;
type FormValues = z.infer<typeof formSchema>;

export const MyMindDialog = () => {
  const form = useForm<FormValues>({
    defaultValues: defaultMyMindItem,
    resolver: zodResolver(formSchema),
  });

  return (
    <SectionDialog<FormValues> id="myMind" form={form} defaultValues={defaultMyMindItem}>
      <div className="grid grid-cols-1 gap-4 p-2 sm:grid-cols-2">
        <FormField
          name="label"
          control={form.control}
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{t`Title`}</FormLabel>
              <FormControl>
                <Input {...field} autoCapitalize="words" leadingIcon={<TextT size={15} />} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="description"
          control={form.control}
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{t`Description`}</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  rows={3}
                  className="resize-none rounded-lg border-gray-200 text-[14px] focus-visible:border-[#268599] focus-visible:shadow-[0_0_0_3px_rgba(38,133,153,0.12)] focus-visible:outline-none"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="percentage"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t`Weight`}</FormLabel>
              <FormControl>
                <div className="flex items-center gap-3">
                  <Slider
                    min={0}
                    max={100}
                    step={1}
                    value={[field.value]}
                    onValueChange={([v]) => field.onChange(v)}
                    className="flex-1"
                  />
                  <span className="w-14 shrink-0 text-right font-mono text-[14px] font-semibold text-gray-700">
                    {field.value} %
                  </span>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="color"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t`Color`}</FormLabel>
              <FormControl>
                <div className="flex flex-wrap gap-2 pt-1">
                  {/* "Auto" option (empty = use palette index color) */}
                  <button
                    type="button"
                    onClick={() => field.onChange("")}
                    title={t`Auto`}
                    className={cn(
                      "flex size-8 items-center justify-center rounded-lg border-2 text-[10px] font-bold text-gray-400 transition-all",
                      !field.value
                        ? "border-[#268599] bg-gray-50 text-[#268599]"
                        : "border-gray-200 bg-gray-50 hover:border-gray-300",
                    )}
                  >
                    A
                  </button>
                  {PALETTE.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => field.onChange(color)}
                      title={color}
                      className={cn(
                        "size-8 rounded-lg transition-all",
                        field.value === color
                          ? "ring-2 ring-[#268599] ring-offset-2"
                          : "ring-0 hover:ring-2 hover:ring-gray-300 hover:ring-offset-1",
                      )}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </SectionDialog>
  );
};
